import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pushPendingShortlist } from "@/lib/pendingIntent";
import { useShortlistWriter } from "@/lib/useShortlistWriter";
import PinToCompareButton from "@/components/app/PinToCompareButton";
import TabWriteup from "@/components/app/university/TabWriteup";
import FirmSearchBox from "@/components/app/FirmSearchBox";
import VerifiedByUniBadge from "@/components/app/VerifiedByUniBadge";
import YourFeeHereTile from "@/components/app/YourFeeHereTile";
import OfstedChip from "@/components/app/OfstedChip";
import { resolveOfferRate, offerRateChipClasses, offerRateChipLabel, offerRateMetricLabel } from "@/lib/offerRate";
import { rankLabel } from "@/lib/rankLabel";
import PathwaySEO from "@/components/app/PathwaySEO";
import TabCourse from "@/components/app/university/TabCourse";
import ApplicationTimeline from "@/components/app/university/ApplicationTimeline";
import SalaryBySubject from "@/components/app/university/SalaryBySubject";
import ScholarshipsCard from "@/components/app/university/ScholarshipsCard";
import NotableAlumni from "@/components/app/university/NotableAlumni";
import UniAlumniMentors from "@/components/app/university/UniAlumniMentors";
import UpcomingFirmEvents from "@/components/app/university/UpcomingFirmEvents";
import StudySpacesCard from "@/components/app/university/StudySpacesCard";
import InterviewPrep from "@/components/app/university/InterviewPrep";
import FacultyHighlights from "@/components/app/university/FacultyHighlights";
import TabScholarships from "@/components/app/university/TabScholarships";
import MatriculationCountdown from "@/components/app/university/MatriculationCountdown";
import PlacementStrip from "@/components/app/university/PlacementStrip";
import { TabSplit } from "@/components/app/university/_shell";
import TabInternational from "@/components/app/university/TabInternational";
import TabOutcomes from "@/components/app/university/TabOutcomes";
import StudentVoiceTab from "@/components/app/StudentVoiceTab";
import NssSectorBenchmark from "@/components/app/NssSectorBenchmark";
import NssTrendSparkline from "@/components/app/NssTrendSparkline";
import StudentExperienceTrendSparkline from "@/components/app/StudentExperienceTrendSparkline";
import SalarySourceBadge from "@/components/app/SalarySourceBadge";
import TabLife from "@/components/app/university/TabLife";
import TabTeaching from "@/components/app/university/TabTeaching";
import TabOutcomesExtras from "@/components/app/university/TabOutcomesExtras";
import TabAdmissionExtras, {
  AcceptanceRateHero,
  TariffHistogramCard,
} from "@/components/app/university/TabAdmissionExtras";
import TabInternationalExtras from "@/components/app/university/TabInternationalExtras";
import TabWarnings from "@/components/app/university/TabWarnings";
import TabOverviewRadar from "@/components/app/university/TabOverviewRadar";
import TabReviews from "@/components/app/university/TabReviews";
import ResearchSignalChip from "@/components/app/university/ResearchSignalChip";
import WuaRankChip from "@/components/app/university/WuaRankChip";
import UniRankingChips from "@/components/app/university/UniRankingChips";
import UniRankingHero from "@/components/app/university/UniRankingHero";
import SameNameWarning from "@/components/app/university/SameNameWarning";
import RelatedGuideLink from "@/components/app/university/RelatedGuideLink";
import LocalAdmissionsCard from "@/components/app/university/LocalAdmissionsCard";
import { usePrimers, primerCodeForCountry } from "@/lib/usePrimers";
import {
  MapPin, Bookmark, AlertTriangle, CheckCircle2,
  ShieldCheck,
} from "lucide-react";

function DataRow({ k, v, note }) {
  return (
    <div className="data-row">
      <div className="data-key">{k}</div>
      <div className="data-val">
        {v}
        {note && (
          <div className="text-[10px] text-amber-800 font-underground-sc tracking-wider mt-0.5">
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

// Map internal `institution_type` slugs to human-facing badge labels.
// Kept small and neutral — no marketing spin, no rank aliases.
function institutionTypeLabel(t) {
  return {
    further_education_college: "Further education college",
    further_education_college_group: "FE college group",
    university_centre: "University centre",
    specialist_conservatoire: "Conservatoire",
    specialist_he_provider: "Specialist HE provider",
    specialist_he_college: "Specialist HE college",
    private_he_provider: "Private HE provider",
    conservatoire: "Conservatoire",
  }[t] || "Higher education provider";
}

export default function UniversityDetail() {
  const { id } = useParams();
  // Deep-link tab reading: `/uni/:id?tab=life` → Tabs opens the Life
  // pane on mount and any subsequent tab click writes the query
  // string back, so deep-links from the DegreeDetail interlink chips
  // (Feb 2026 Phase 2) survive refreshes and share links.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["admission", "course", "cost", "scholarships", "outcomes", "life", "international", "teaching", "reviews", "studentVoice", "warnings", "overview"];
  const requestedTab = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(requestedTab) ? requestedTab : "admission";
  const setActiveTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (!next || next === "admission") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };
  const [uni, setUni] = useState(null);
  const [claimState, setClaimState] = useState(null);
  const { format } = useCurrency();
  const { user, login } = useAuth();
  const { addToShortlist: writeShortlist } = useShortlistWriter();
  // Primer index (country codes we have an "How to apply in X" page for).
  // Drives the Admission-tab link; hidden when we don't yet cover the
  // uni's country so students never land on a 404.
  const primers = usePrimers();

  useEffect(() => {
    api.get(`/universities/${id}`).then((r) => setUni(r.data)).catch(() => {});
    // Featured courses load page by page inside the Courses tab (TabCourse).
    // Public claim/ownership signal for the Verified-by-uni badge + Claim CTA.
    api.get(`/claims/uni/${id}`).then((r) => setClaimState(r.data)).catch(() => setClaimState(null));
  }, [id]);

  // For `source_tier="directory"` unis with NO description yet, inject
  // `<meta name="robots" content="noindex,follow">` so crawlers don't
  // index a thin profile. Once the LLM writeup lands (Phase B of the
  // FE-college promotion), the description is populated and the page
  // becomes substantial enough to index — the meta comes off.
  useEffect(() => {
    if (!uni || uni.source_tier !== "directory") return;
    if (uni.description) return; // Substantial page — index normally.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,follow";
    meta.setAttribute("data-wua-noindex", "directory-tier");
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, [uni]);

  // Deep-link scroll — when a counsellor's email hits
  // `/uni/<id>?tab=outcomes`, land the page directly on the salary
  // sparkline instead of at the top of the tabs bar. Waits for the
  // uni fetch so TabsContent has mounted.
  useEffect(() => {
    if (!uni) return;
    if (activeTab !== "outcomes") return;
    const t = setTimeout(() => {
      const el =
        document.querySelector('[data-testid="salary-by-subject"]') ||
        document.querySelector('[data-testid="uni-outcomes-anchor"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uni, activeTab]);

  // Fire a lightweight analytics ping the FIRST time a visitor lands
  // on this uni page with `?tab=outcomes` (i.e. inbound counsellor
  // email deep-link). Fire-and-forget; failures never block UI.
  const outcomesPingedRef = useRef(false);
  useEffect(() => {
    if (outcomesPingedRef.current) return;
    if (requestedTab !== "outcomes") return;
    if (!uni?.id) return;
    outcomesPingedRef.current = true;
    let anon = "";
    try { anon = localStorage.getItem("wua:journey:anon") || ""; } catch {}
    if (!anon) return;
    api.post("/journey/nav_event", {
      anonymous_id: anon,
      event: "deep_link_outcomes",
      meta: { university_id: uni.id },
    }).catch(() => {});
  }, [uni?.id, requestedTab]);

  if (!uni) return null;

  const totalAnnual = (uni.tuition_intl_annual_usd || 0) + (uni.accommodation_annual_usd || 0)
    + (uni.food_annual_usd || 0) + (uni.health_insurance_annual_usd || 0)
    + (uni.books_annual_usd || 0) + (uni.travel_home_annual_usd || 0) + (uni.visa_annual_usd || 0);
  const totalDegree = totalAnnual * (uni.degree_years_typical || 3);

  // Resolve the links to our deeper editorial guides. Both resolve to
  // `null` when the target isn't live (missing city_slug or no primer
  // yet) — RelatedGuideLink then renders nothing, per the "hide
  // gracefully" product rule.
  //
  // `uni.country` is stored inconsistently (2-letter like "UK" for
  // some, full names like "Sweden" / "Hong Kong" for others). The
  // helper handles both so SE / HK uni pages pick up their primer
  // links the same way UK / US / AE already do.
  const primerCountryCode = primerCodeForCountry(uni.country, primers.codes);
  const primerCountryName = primerCountryCode ? (primers.byCode[primerCountryCode]?.name || uni.country) : null;
  const cityGuideTo = uni.city_slug ? `/cities/${uni.city_slug}` : null;
  const primerGuideTo = primerCountryCode ? `/apply/${primerCountryCode}` : null;

  const addToShortlist = async () => {
    if (!user) {
      pushPendingShortlist(uni.id, uni.name);
      toast(`Sign in to save ${uni.name} — we'll add it for you automatically.`);
      setTimeout(login, 700);
      return;
    }
    try {
      await writeShortlist({ university_id: uni.id });
      toast.success(`${uni.name} added to your shortlist`);
    } catch { toast.error("Could not add"); }
  };

  return (
    <div>
      <PathwaySEO
        title={`${uni.name} · Sourced admissions, cost, cohort outcomes`}
        description={
          `${uni.name}${uni.country ? ` (${uni.country})` : ""} on WhichUniversityAdvisor: ` +
          (uni.acceptance_rate != null
            ? `${(uni.acceptance_rate * 100).toFixed(1)}% acceptance · `
            : "") +
          (uni.avg_starting_salary_usd
            ? `$${uni.avg_starting_salary_usd.toLocaleString()} median grad salary · `
            : "") +
          "sourced destinations, honest cost-of-living, and firm-by-firm salary bands. Not another ranking."
        }
        path={`/uni/${uni.id}`}
      />
      {/* Hero — prefers cached Google Places photo (weekly-refreshed
          server-side). Falls back to legacy `image_url` when the
          Places resolver hasn't reached this uni yet. Attribution
          byline is required by Google's Places TOS. */}
      <section className="relative border-b border-stone-200">
        <div className="aspect-[21/8] max-h-[420px] overflow-hidden relative">
          <img
            src={uni.photo_url || uni.image_url}
            alt={uni.name}
            className="w-full h-full object-cover"
            data-testid="uni-hero-image"
            onError={(e) => { if (uni.image_url && e.target.src !== uni.image_url) e.target.src = uni.image_url; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          {uni.photo_url && (
            <div className="absolute bottom-3 right-3 text-[10px] font-underground-sc tracking-wider text-white/80 bg-black/40 backdrop-blur-sm rounded px-1.5 py-0.5"
                 data-testid="uni-hero-photo-credit">
              Image · Google
              {Array.isArray(uni.photo_attributions) && uni.photo_attributions[0]?.displayName && (
                <span className="ml-1 text-white/60">/ {uni.photo_attributions[0].displayName}</span>
              )}
            </div>
          )}
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 -mt-24 relative z-10 pb-8">
          <div className="bg-white border border-stone-200 rounded-lg p-6 lg:p-8 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="label-uppercase">
                  {uni.country}
                  {rankLabel(uni) ? ` · ${rankLabel(uni)}` : ""}
                </div>
                <h1 className="font-editorial text-4xl sm:text-5xl mt-2 tracking-tight">{uni.name}</h1>
                {uni.source_tier === "directory" && (
                  uni.description ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <div
                        data-testid="uni-institution-type-badge"
                        className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-2.5 py-0.5 text-[11px] font-underground-sc tracking-wider text-stone-700"
                      >
                        {institutionTypeLabel(uni.institution_type)}
                      </div>
                      <OfstedChip
                        rating={uni.ofsted_rating}
                        inspectionDate={uni.ofsted_inspection_date}
                        size="md"
                      />
                    </div>
                  ) : (
                    <div
                      data-testid="uni-directory-notice"
                      className="mt-3 inline-flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/70 px-3 py-2 text-[12px] text-amber-900 max-w-2xl"
                      role="note"
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-[2px]" aria-hidden />
                      <span>
                        <span className="font-semibold uppercase tracking-wider text-[10px] font-mono">
                          Directory listing
                        </span>
                        <span className="block mt-0.5">
                          We haven't published an editorial review of this
                          institution yet. Data below is from public regulator
                          sources only — treat course details as indicative
                          and confirm on the provider's own site before applying.
                        </span>
                      </span>
                    </div>
                  )
                )}
                <div className="flex items-center gap-2 mt-2 text-stone-600 text-sm">
                  <MapPin className="h-3 w-3" />
                  {uni.city_slug ? (
                    <Link
                      to={`/cities/${uni.city_slug}`}
                      data-testid="uni-detail-city-link"
                      className="underline decoration-dotted decoration-stone-400 hover:text-[#002147] hover:decoration-[#002147]"
                    >
                      {uni.city}
                    </Link>
                  ) : (
                    <span>{uni.city}</span>
                  )}
                  {(() => {
                    // Feb 2026 — two field names in flight across
                    // ingest waves: `established` (older) and
                    // `founded_year` (newer editorial seeds). Prefer
                    // whichever is populated; suppress the "Founded"
                    // line entirely when both are blank.
                    const founded = uni.established || uni.founded_year;
                    const bits = [];
                    if (uni.campus_type) bits.push(uni.campus_type);
                    if (founded) bits.push(`Founded ${founded}`);
                    return bits.length
                      ? <span>· {bits.join(" · ")}</span>
                      : null;
                  })()}
                </div>
                {/* Research signal — OpenAlex-derived band chip. Silent
                    for unis we haven't enriched yet (Wave 1 covers the
                    top 40 UK+US flagships; later waves extend coverage). */}
                {uni.enrichment && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ResearchSignalChip enrichment={uni.enrichment} universityName={uni.name} />
                    <WuaRankChip uniId={uni.id} />
                  </div>
                )}
                {claimState?.claimed && (
                  <div className="mt-3">
                    <VerifiedByUniBadge
                      lastUpdatedAt={claimState.last_uni_update_at}
                      updatedBy={claimState.last_uni_update_by}
                    />
                  </div>
                )}
                <UniRankingHero uniId={uni.id} />
                <UniRankingChips uniId={uni.id} />
                {/* Feb 2026 · Task 5 follow-on — same-name disambiguation
                    strip. Silent unless the uni is in the audited
                    known-different registry (Trinity CT vs Trinity TX,
                    Boston College vs Boston U, etc.). */}
                <SameNameWarning uni={uni} />
                <p className="mt-4 max-w-2xl text-stone-700 leading-relaxed">{uni.description}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Button data-testid="add-shortlist-btn" onClick={addToShortlist}
                        className="bg-[#002147] hover:bg-[#001A38] text-white rounded-md">
                  <Bookmark className="h-4 w-4 mr-1.5" /> Save to shortlist
                </Button>
                <PinToCompareButton uni={uni} />
                <Link to={`/admission?uni=${uni.id}`}>
                  <Button variant="outline" size="sm" className="w-full rounded-md">
                    Check my chances
                  </Button>
                </Link>
                {claimState && !claimState.claimed && (
                  <Link to={`/uni/${uni.id}/claim`} data-testid="uni-detail-claim-cta">
                    <Button variant="ghost" size="sm" className="w-full rounded-md text-emerald-800 hover:bg-emerald-50 border border-dashed border-emerald-200">
                      <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Own this university? Claim it
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Summary bento */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-stone-200 pt-6">
              <div data-testid="uni-summary-offer-rate">
                {(() => {
                  const or = resolveOfferRate({ uni });
                  if (!or) {
                    return (
                      <>
                        <div className="label-uppercase">Offer rate</div>
                        <div className="font-chip text-2xl font-semibold mt-1">—</div>
                      </>
                    );
                  }
                  const isStub = or.source === "legacy_editorial_stub";
                  return (
                    <>
                      <div className="label-uppercase flex items-center gap-2">
                        {offerRateMetricLabel(or)}
                        <span
                          data-testid="offer-rate-provenance-chip"
                          title={or.label}
                          className={`inline-flex items-center rounded-full border px-1.5 py-[1px] text-[9px] font-underground-sc tracking-wider ${offerRateChipClasses(or.source)}`}
                        >
                          {offerRateChipLabel(or.source)}
                        </span>
                      </div>
                      <div className={`font-chip text-2xl font-semibold mt-1 ${isStub ? "text-stone-500" : ""}`}>
                        {(or.value * 100).toFixed(1)}%
                      </div>
                      {isStub && (
                        <div className="text-[10px] text-amber-800 mt-0.5 leading-snug">
                          Awaiting verified UCAS / uni data — number is a seed estimate.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <div className="label-uppercase">Total annual cost</div>
                <div className="font-chip text-2xl font-semibold mt-1">
                  {totalAnnual ? format(totalAnnual) : "—"}
                </div>
                {/* US-public-flagship two-tier disclosure. Non-null
                    in_state / out_state means we've hydrated the
                    honest resident vs non-resident split, so we show
                    both alongside the "international = out-of-state"
                    default the bento already renders. */}
                {(uni.tuition_in_state_usd != null || uni.tuition_out_state_usd != null) && (
                  <div
                    data-testid="uni-us-public-fee-split"
                    className="mt-1 text-[10px] font-mono text-stone-500 leading-tight"
                  >
                    {uni.tuition_in_state_usd != null && (
                      <div>In-state: <span className="text-stone-700">{format(uni.tuition_in_state_usd)}</span></div>
                    )}
                    {uni.tuition_out_state_usd != null && (
                      <div>Out-of-state / intl: <span className="text-stone-700">{format(uni.tuition_out_state_usd)}</span></div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div className="label-uppercase">Grad. salary</div>
                <div className="font-chip text-2xl font-semibold mt-1">
                  {uni.avg_starting_salary_usd
                    ? format(uni.avg_starting_salary_usd)
                    : "—"}
                </div>
                <SalarySourceBadge
                  salarySource={uni.outcomes?.salary_source || uni.salary_source}
                  salaryIngestedAt={uni.outcomes?.salary_ingested_at}
                  className="mt-1"
                />
                {/* Two-tier US Scorecard disclosure: 1-yr = starting
                    salary, 5-yr = career-trajectory. Rendered as a
                    sub-caption so families see BOTH the "first job"
                    and "5 years in" numbers, honestly labelled. */}
                {(uni.median_earnings_1yr_usd || uni.median_earnings_5yr_usd) && (
                  <div
                    data-testid="uni-scorecard-earnings-split"
                    className="mt-1 text-[10px] font-mono text-stone-500 leading-tight"
                  >
                    {uni.median_earnings_1yr_usd && (
                      <div>1yr Scorecard: <span className="text-stone-700">{format(uni.median_earnings_1yr_usd)}</span></div>
                    )}
                    {uni.median_earnings_5yr_usd && (
                      <div>5yr Scorecard: <span className="text-stone-700">{format(uni.median_earnings_5yr_usd)}</span></div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div className="label-uppercase">Satisfaction</div>
                <div className="font-chip text-2xl font-semibold mt-1">
                  {uni.satisfaction_score != null ? `${uni.satisfaction_score}/5` : "—"}
                </div>
                {uni.satisfaction_score != null && uni.satisfaction_source && (
                  <div
                    className="mt-1 text-[10px] font-mono text-stone-500 leading-tight"
                    data-testid="satisfaction-source-caption"
                    title={uni.satisfaction_source}
                  >
                    source · {uni.satisfaction_source}
                  </div>
                )}
                {uni.satisfaction_score != null && uni.country === "UK" && (
                  <NssSectorBenchmark
                    satisfactionScore={uni.satisfaction_score}
                    verifiedPct={uni.satisfaction_pct_verified}
                    nResponses={uni.satisfaction_n_responses}
                  />
                )}
                {uni.satisfaction_score != null && uni.country === "UK" && (
                  <NssTrendSparkline uniId={uni.id} />
                )}
                {uni.satisfaction_score != null && (uni.country === "AU" || uni.country === "Australia") && (
                  <StudentExperienceTrendSparkline uniId={uni.id} country={uni.country} />
                )}
                {uni.satisfaction_score != null && (
                  <a
                    href="#studentVoice"
                    data-testid="satisfaction-do-you-agree"
                    onClick={(e) => {
                      e.preventDefault();
                      const trigger = document.querySelector('[data-testid="uni-tab-studentVoice"]');
                      if (trigger) trigger.click();
                      setTimeout(() => {
                        const el = document.querySelector('[data-testid="uni-survey-panel"]');
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 150);
                    }}
                    className="mt-2 inline-block text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
                  >
                    Do you agree? →
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Firm CTA — surfaced above the tabs so uni-page visitors
          think "wait, I want a specific employer" and can jump into
          the reverse-search flow without hunting through tabs. */}
      <section
        data-testid="uni-page-firm-cta"
        className="max-w-7xl mx-auto px-6 lg:px-10 pt-8"
      >
        <div className="rounded-lg border border-[#DDD6FE] bg-gradient-to-r from-[#F5F0FF] to-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="label-uppercase !text-[10px] text-[#4C1D95]">
              Aiming at a specific firm?
            </div>
            <p className="mt-2 text-[14px] text-stone-700 leading-snug">
              See which universities feed graduates into <em>your</em> target employer — 715 firms indexed with sourced destinations data.
            </p>
          </div>
          <div className="w-full sm:w-80 shrink-0">
            <FirmSearchBox variant="default" destination="get-me-to" />
          </div>
        </div>
      </section>

      {/* Tabs (10 sections) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            setActiveTab(v);
            // Smooth-scroll to the earnings/salary block inside the
            // Outcomes tab so the student lands directly on the
            // sparkline instead of at the top of a long tab.
            if (v === "outcomes") {
              // Wait for the TabsContent to mount, then scroll.
              setTimeout(() => {
                const el =
                  document.querySelector('[data-testid="salary-by-subject"]') ||
                  document.querySelector('[data-testid="uni-outcomes-anchor"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 80);
            }
          }}
          className="w-full"
        >
          <TabsList data-testid="uni-tabs" className="flex flex-wrap h-auto w-full justify-start gap-2 bg-transparent p-0 border-b border-stone-200 rounded-none">
            {[
              ["admission", "Admission"],
              ["course", "Courses"],
              ["cost", "Cost"],
              ["scholarships", "Scholarships"],
              ["outcomes", "Outcomes"],
              ["life", "Life"],
              ["international", "International"],
              ["teaching", "Teaching"],
              ["reviews", "Who's Looking?"],
              ["studentVoice", "Student Voice"],
              ["warnings", "Warnings"],
              ["overview", "Overview"],
            ].map(([v, l]) => (
              <TabsTrigger key={v} value={v}
                data-testid={`uni-tab-${v}`}
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-stone-900 data-[state=active]:text-stone-900 text-stone-500 rounded-none px-3 py-2 -mb-px">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Admission */}
          <TabsContent value="admission" className="pt-8 space-y-10">
            {/* Timeline runs at full page width so all 12 months are
                readable without horizontal scroll. */}
            <ApplicationTimeline uniId={uni.id} />

            {/* Two-column reading rail. Left = editorial writeup,
                right = AcceptanceRateHero + Requirements card. The
                Requirements card sits directly under the acceptance
                rate so applicants see the numeric threshold and the
                admissions gates in the same visual block. */}
            <TabSplit
              left={<TabWriteup uniId={uni.id} tab="admission" />}
              right={
                <>
                  <AcceptanceRateHero uni={uni} />
                  <Card className="p-6 border-stone-200">
                    <div className="label-uppercase">Requirements</div>
                    <div className="mt-4">
                      <DataRow k="Required subjects" v={(uni.required_subjects || []).join(", ") || "—"} />
                      <DataRow k="Portfolio" v={uni.portfolio_required ? "Yes" : "No"} />
                      <DataRow k="Interview" v={uni.interview_required ? "Yes" : "No"} />
                      <DataRow k="Entrance test" v={uni.entrance_test || "—"} />
                      <DataRow k="Foundation year" v={uni.foundation_year_available ? "Available" : "Not available"} />
                      {(() => {
                        const or = resolveOfferRate({ uni });
                        if (!or) return <DataRow k="Offer rate" v="—" />;
                        const label = (or.source === "ucas_eoc" || or.source === "uni_offer_stats") ? "Offer rate" : "Acceptance rate";
                        const isStub = or.source === "legacy_editorial_stub";
                        return (
                          <DataRow
                            k={label}
                            v={
                              <span data-testid="acceptance-row-value" className="inline-flex items-center gap-2">
                                <span>{`${(or.value * 100).toFixed(1)}%`}</span>
                                <span
                                  title={or.label}
                                  className={`inline-flex items-center rounded-full border px-1.5 py-[1px] text-[9px] font-underground-sc tracking-wider ${offerRateChipClasses(or.source)}`}
                                >
                                  {offerRateChipLabel(or.source)}
                                </span>
                              </span>
                            }
                            note={isStub ? "Seed default — awaiting verified data" : (or.evidence?.cycle_year ? `Cycle ${or.evidence.cycle_year}` : null)}
                          />
                        );
                      })()}
                    </div>
                  </Card>
                </>
              }
            />

            {/* Histogram (75%) + Typical Admitted Student card (25%)
                side by side. The card anchors the chart's admits with
                what they look like on paper. */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-3">
                <TariffHistogramCard uni={uni} />
              </div>
              <div className="lg:col-span-1">
                <Card className="p-4 border-stone-200 sticky top-4">
                  <div className="label-uppercase !text-[10px]">Typical admitted student</div>
                  <div className="mt-3">
                    {Object.entries(uni.curricula || {}).map(([k, v]) => (
                      <DataRow key={k}
                        k={k.replace("_", " ")}
                        v={<span>min <b>{String(v.min)}</b> · typical <b>{String(v.typical)}</b></span>}
                      />
                    ))}
                  </div>
                </Card>
              </div>
            </div>

            {/* Interview / personal statement prep. Full width so the
                curated bullets read like a leader column. */}
            <InterviewPrep uniId={uni.id} />

            {uni.test_policy_note && (
              <div data-testid="test-policy-callout"
                   className={`rounded-md border-l-4 p-4 ${
                     uni.test_policy === "required"
                       ? "border-red-700 bg-red-50"
                       : uni.test_policy === "blind"
                       ? "border-stone-700 bg-stone-100"
                       : "border-emerald-700 bg-emerald-50"
                   }`}>
                <div className="label-uppercase !text-[10px]">
                  SAT / ACT policy · {uni.test_policy === "required" ? "REQUIRED" : uni.test_policy === "blind" ? "TEST-BLIND" : uni.test_policy === "optional" ? "TEST-OPTIONAL" : "NOT REQUIRED"}
                </div>
                <p className="text-sm mt-1 text-stone-800 leading-relaxed">{uni.test_policy_note}</p>
              </div>
            )}

            {/* Country-level "How to apply" primer — appears only when we
                already carry a primer for this country, otherwise the
                link is suppressed so students never hit a dead route. */}
            <RelatedGuideLink
              to={primerGuideTo}
              eyebrow="Country-level guide"
              title={primerCountryName ? `How to apply in ${primerCountryName}` : null}
              blurb="Routes, deadlines, visas and English requirements — written for international applicants."
              testId="uni-detail-admission-country-primer-link"
            />

            {/* P2 depth — fee tiers / scholarship corridors / entry
                routes for the markets WUA goes deep on (AE/SG/HK/CN).
                Renders nothing for markets not in that set, so UK/US
                pages are unaffected. */}
            <LocalAdmissionsCard country={uni.country} />
          </TabsContent>

          {/* Course — featured (editorial) degrees for this uni.
              Falls back to the legacy `uni.courses` hand-coded list
              only when we haven't curated any editorial records yet. */}
          <TabsContent value="course" className="pt-8">
            <TabCourse uni={uni} format={format} />
          </TabsContent>

          {/* Cost */}
          <TabsContent value="cost" className="pt-8">
            {(() => {
              const startingSalaryUsd =
                uni.avg_starting_salary_usd
                || (uni.leo3_median_gbp && uni.country === "UK"
                    ? uni.leo3_median_gbp * 1.27
                    : uni.starting_salary_usd || null);
              const paybackYears = startingSalaryUsd && totalDegree
                ? +(totalDegree / startingSalaryUsd).toFixed(1)
                : null;
              const paybackTone =
                paybackYears == null ? { c: "#002147", label: "Awaiting salary data" } :
                paybackYears <= 3    ? { c: "#065F46", label: "Top-decile ROI" } :
                paybackYears <= 5    ? { c: "#B45309", label: "Sector-average ROI" } :
                                        { c: "#9F1239", label: "Slow · check funding" };
              return (
                <TabSplit
                  left={<TabWriteup uniId={uni.id} tab="cost" />}
                  right={
                    <>
                      <div>
                        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] mb-2 block">
                          01 · The Financial Picture
                        </span>
                        <h2 className="font-editorial text-2xl md:text-3xl text-[#002147] leading-tight border-b border-[#002147]/10 pb-4 mb-6">
                          What this degree costs — and how fast it pays back
                        </h2>
                        <div className="mb-5 max-w-lg">
                          <YourFeeHereTile
                            country={uni.country}
                            intlFeeUsd={uni.tuition_intl_annual_usd}
                            homeFeeUsd={uni.tuition_domestic_annual_usd}
                            testId="uni-detail-your-fee-tile"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#002147]/10 border border-[#002147]/10" data-testid="cost-payback-hero">
                          <div className="flex flex-col p-6 md:p-8 border-t-4 border-[#002147] bg-[#F3F1EC]">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">Total degree cost · intl.</span>
                            <div className="text-3xl md:text-4xl font-serif text-[#002147] mt-2 leading-none">{format(totalDegree)}</div>
                            <div className="mt-3 text-xs font-mono text-[#002147]/60">{uni.degree_years_typical}-year programme</div>
                          </div>
                          <div className="flex flex-col p-6 md:p-8 border-t-4 border-[#002147] bg-[#F3F1EC]">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">Typical starting salary</span>
                            <div className="text-3xl md:text-4xl font-serif text-[#002147] mt-2 leading-none">
                              {startingSalaryUsd ? format(startingSalaryUsd) : "—"}
                            </div>
                            <div className="mt-3 text-xs font-mono text-[#002147]/60">
                              {uni.country === "UK" ? "3-yr LEO median · HESA" : "10-yr Scorecard median"}
                            </div>
                          </div>
                          <div
                            data-testid="cost-payback-years"
                            className="flex flex-col p-6 md:p-8 border-t-4 bg-[#F3F1EC]"
                            style={{ borderTopColor: paybackTone.c }}
                          >
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">Payback years</span>
                            <div className="text-3xl md:text-4xl font-serif mt-2 leading-none" style={{ color: paybackTone.c }}>
                              {paybackYears != null ? `${paybackYears}` : "—"}
                              {paybackYears != null && <span className="font-mono text-base align-baseline ml-2 text-[#002147]/60">yr</span>}
                            </div>
                            <div className="mt-3 text-xs font-underground-sc tracking-widest" style={{ color: paybackTone.c }}>
                              {paybackTone.label}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-12 gap-6">
                        <div className="md:col-span-7 flex flex-col rounded-sm border border-[#002147]/10 bg-transparent">
                          <div className="font-mono text-xs uppercase tracking-widest text-[#002147]/60 px-4 pt-4 pb-2">Annual cost · international</div>
                          <DataRow k="Tuition (intl.)" v={format(uni.tuition_intl_annual_usd)} />
                          <DataRow k="Tuition (domestic)" v={format(uni.tuition_domestic_annual_usd)} />
                          <DataRow k="Accommodation" v={format(uni.accommodation_annual_usd)} />
                          <DataRow k="Food & daily living" v={format(uni.food_annual_usd)} />
                          <DataRow k="Health insurance" v={format(uni.health_insurance_annual_usd)} />
                          <DataRow k="Books & equipment" v={format(uni.books_annual_usd)} />
                          <DataRow k="Travel home" v={format(uni.travel_home_annual_usd)} />
                          <DataRow k="Visa" v={format(uni.visa_annual_usd)} />
                          <DataRow k="Expected fee growth" v={`${uni.annual_fee_growth_pct}% / year`} />
                          <DataRow k="Degree duration" v={`${uni.degree_years_typical} years`} />
                        </div>
                        <div className="md:col-span-5">
                          <div className="p-6 md:p-8 bg-[#002147] text-[#F3F1EC] rounded-sm">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[#F3F1EC]/60">Total · international</span>
                            <div className="mt-4">
                              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F3F1EC]/60">Annual net</div>
                              <div className="font-serif text-3xl mt-1 leading-none">{format(totalAnnual)}</div>
                            </div>
                            <div className="mt-6">
                              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F3F1EC]/60">Full degree · {uni.degree_years_typical} yrs</div>
                              <div className="font-serif text-3xl mt-1 leading-none">{format(totalDegree)}</div>
                            </div>
                            <p className="mt-6 text-xs text-[#F3F1EC]/70 leading-relaxed font-sans">
                              Numbers convert live from USD to your selected currency. Change the currency in the top bar.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  }
                />
              );
            })()}
            {/* CTA linking to the dedicated Scholarships tab. Cost
                gives the sticker price; the Scholarships tab explains
                how to pay less. */}
            <button
              type="button"
              onClick={() => {
                const trigger = document.querySelector('[data-testid="uni-tab-scholarships"]');
                if (trigger) trigger.click();
                window.scrollTo({ top: trigger?.getBoundingClientRect().top + window.scrollY - 100, behavior: "smooth" });
              }}
              className="mt-6 w-full text-left bg-[#F3F1EC] border-l-4 border-[#7C3AED] p-5 hover:bg-[#7C3AED]/[0.04] transition-colors flex items-center justify-between gap-4"
              data-testid="cost-see-scholarships-cta"
            >
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] mb-1">
                  Bring the sticker price down
                </div>
                <div className="font-serif text-lg text-[#002147]">
                  See scholarships, bursaries, and national schemes →
                </div>
                <div className="mt-1 font-sans text-sm text-[#002147]/70">
                  Most students pay less than the numbers above. The Scholarships tab explains what's available and how to stack it.
                </div>
              </div>
            </button>
            {/* City cost-of-living deep link — written for the "but what
                does rent actually run?" question. Hidden if we don't
                have an editorial guide for the uni's city. */}
            <div className="mt-4">
              <RelatedGuideLink
                to={cityGuideTo}
                eyebrow="Beyond tuition"
                title={uni.city ? `See cost of living in ${uni.city}` : null}
                blurb="Rent, groceries, transport and a monthly student budget — all currency-aware."
                testId="uni-detail-cost-city-guide-link"
              />
            </div>
          </TabsContent>

          {/* Scholarships — dedicated tab replacing the inline card. */}
          <TabsContent value="scholarships" className="pt-0">
            <TabScholarships uni={uni} />
          </TabsContent>

          {/* Outcomes */}
          <TabsContent value="outcomes" className="pt-8" data-testid="uni-outcomes-anchor">
            <TabOutcomesExtras uni={uni} />
            <PlacementStrip uniId={uni.id} />
            <SalaryBySubject uniId={uni.id} format={format} />
            <TabOutcomes uni={uni} format={format} />
          </TabsContent>

          {/* Life */}
          <TabsContent value="life" className="pt-8 space-y-6">
            <TabSplit
              left={<TabWriteup uniId={uni.id} tab="life" />}
              right={<TabLife uni={uni} format={format} />}
            />
            <RelatedGuideLink
              to={cityGuideTo}
              eyebrow="City-level guide"
              title={uni.city ? `Explore the ${uni.city} guide` : null}
              blurb="Neighbourhoods, transport, safety and the student quotes we lifted from Reddit."
              testId="uni-detail-life-city-guide-link"
            />
          </TabsContent>

          {/* International */}
          <TabsContent value="international" className="pt-8">
            <TabInternationalExtras uni={uni} />
            <TabInternational uni={uni} />
          </TabsContent>

          {/* Teaching */}
          <TabsContent value="teaching" className="pt-8">
            <TabSplit
              left={<TabWriteup uniId={uni.id} tab="teaching" />}
              right={
                <>
                  <TabTeaching uni={uni} />
                  <StudySpacesCard uniId={uni.id} />
                  <FacultyHighlights uniId={uni.id} />
                </>
              }
            />
          </TabsContent>

          {/* Reviews (structured placeholder) */}
          <TabsContent value="reviews" className="pt-8">
            <TabReviews uni={uni} />
          </TabsContent>

          {/* Student Voice (v9.64) — first-party university-wide survey */}
          <TabsContent value="studentVoice" className="pt-8" data-testid="uni-tab-content-studentVoice">
            <StudentVoiceTab universityId={uni.id} />
          </TabsContent>

          {/* Warnings */}
          <TabsContent value="warnings" className="pt-8">
            <TabWarnings uni={uni} />
          </TabsContent>

          {/* Overview */}
          <TabsContent value="overview" className="pt-8 space-y-10">
            <MatriculationCountdown uniId={uni.id} />
            <TabSplit
              left={
                <>
                  <TabWriteup uniId={uni.id} tab="overview" />
                  {uni.description && (
                    <p className="mt-6 font-serif text-sm text-[#002147]/60 leading-relaxed border-t border-[#002147]/10 pt-4">
                      {uni.description}
                    </p>
                  )}
                </>
              }
              right={
                <>
                  <TabOverviewRadar uni={uni} />
                  <UpcomingFirmEvents universityId={uni.id} />
                </>
              }
            />
            {/* Notable alumni sits below the split as a full-width
                portrait strip so the reader gets a broad "who studied
                here" impression after the radar comparison. */}
            <NotableAlumni uniId={uni.id} />
            <UniAlumniMentors universityId={uni.id} />
          </TabsContent>
        </Tabs>
      </section>

      {/* Directory-tier "All courses at <Uni>" section was retired
          from University Detail on 2026-02-28. The Courses tab now
          carries the full curated list; showing an additional
          HESA-only long-tail below duplicated content and looked
          orphaned on unis without editorial coverage. The
          <DirectoryCoursesList/> component is still used by Advanced
          Search (`DirectoryMatchesSection`). */}
    </div>
  );
}
